import { createContext, useContext } from "react"

/**
 * Merge-tag registry for the email builder.
 *
 * Mirrors the legacy Unlayer config (apps/connect/src/common/email/
 * unlayer-editor.tsx) **exactly** — same categories, same tag ids,
 * same `value` strings, same `sample` values. The Stampede send
 * service substitutes `{{ … }}` placeholders per-recipient at send
 * time using a Mustache-style template engine, so:
 *   - double-brace `{{ x.y }}` → HTML-escaped substitution
 *   - triple-brace `{{{ x.y }}}` → raw substitution (used for the
 *     voucher QR which is itself an HTML <img> string)
 *
 * Adding tags here is intentionally a manual step: the service has
 * to know how to substitute every key on the wire. New tags should
 * land in this file ONLY after backend confirms support.
 */

export interface MergeTag {
  /** Stable id within the category (e.g. `first`). */
  id: string
  /** Human label shown in the picker (e.g. "First Name"). */
  name: string
  /**
   * Exact string inserted into copy. Includes the surrounding
   * braces so the picker / agent / autocomplete all agree on a
   * single canonical wire form.
   */
  value: string
  /**
   * Realistic sample value used in the canvas preview so the
   * operator sees what the email will look like rendered. Not
   * sent to the service.
   */
  sample: string
  /**
   * Optional icon hint mirroring Unlayer's config (e.g. 'qr-code'
   * for the voucher QR). Currently unused by the picker — kept
   * for parity in case we render category-specific glyphs later.
   */
  icon?: string
}

export interface MergeTagCategory {
  /** Stable category key (e.g. `profile`). */
  id: string
  /** Human label for the picker section header. */
  name: string
  tags: MergeTag[]
}

export type MergeTagRegistry = MergeTagCategory[]

/**
 * The built-in registry — exact 1:1 port of the Unlayer config. The
 * builder doesn't hardcode this; it's exported so apps can pass
 * their own registry to <EmailBuilder mergeTags={…}/> and over time
 * we can drive it from a backend endpoint.
 */
export const defaultMergeTagRegistry: MergeTagRegistry = [
  {
    id: "profile",
    name: "Profile Information",
    tags: [
      {
        id: "first",
        name: "First Name",
        value: "{{ profile.first }}",
        sample: "John",
      },
      {
        id: "last",
        name: "Last Name",
        value: "{{ profile.last }}",
        sample: "Doe",
      },
      {
        id: "email",
        name: "Email",
        value: "{{ profile.email }}",
        sample: "user@example.com",
      },
    ],
  },
  {
    id: "voucher_code",
    name: "Voucher Details",
    tags: [
      {
        id: "qr",
        name: "Voucher QR Image",
        // Triple-brace = raw / unescaped substitution. The service
        // injects a full <img …> string here, so HTML-escaping
        // would break it.
        value: "{{{ voucher_code.qr }}}",
        sample: "https://via.placeholder.com/150",
        icon: "qr-code",
      },
      {
        id: "text",
        name: "Voucher Code",
        value: "{{ voucher_code.text }}",
        sample: "1234ABCD",
      },
      {
        id: "expiry",
        name: "Voucher Expiry",
        value: "{{ voucher_code.expiry }}",
        sample: "MM/DD/YYYY",
      },
    ],
  },
]

/**
 * Flatten a registry into a `wire-string → tag` lookup so the
 * canvas preview substitution can swap placeholders to samples in
 * a single regex pass.
 */
export function buildMergeTagLookup(
  registry: MergeTagRegistry,
): Record<string, MergeTag> {
  const out: Record<string, MergeTag> = {}
  for (const category of registry) {
    for (const tag of category.tags) {
      out[tag.value] = tag
    }
  }
  return out
}

/**
 * Single regex covering both `{{ x.y }}` and `{{{ x.y }}}` —
 * matches the full token (braces included) so the canvas
 * substitution can swap an entire placeholder for a sample value
 * without leaving stray braces behind. Underscores allowed in
 * keys (Unlayer uses `voucher_code`).
 */
const MERGE_TAG_RE = /\{\{\{?\s*([\w.]+)\s*\}?\}\}/g

/**
 * Replace every merge-tag placeholder in `html` with its sample
 * value, for the canvas / live preview only. The compose pipeline
 * deliberately does NOT call this — the service must see the raw
 * placeholders so it can substitute per-recipient at send time.
 *
 * Unknown keys are left untouched so the operator can spot typos.
 */
export function substituteMergeTagSamples(
  html: string,
  registry: MergeTagRegistry,
): string {
  if (!html) return html
  const lookup = buildMergeTagLookup(registry)
  return html.replace(MERGE_TAG_RE, (match) => {
    const tag = lookup[match.replace(/\s+/g, " ")] ?? lookup[normaliseSpacing(match)]
    return tag ? tag.sample : match
  })
}

function normaliseSpacing(s: string): string {
  // Convert `{{x.y}}` (no spaces) to `{{ x.y }}` so the lookup hits
  // even if the operator typed it dense. Triple-brace handled the
  // same way.
  return s
    .replace(/^\{\{\{\s*/, "{{{ ")
    .replace(/^\{\{\s*/, "{{ ")
    .replace(/\s*\}\}\}$/, " }}}")
    .replace(/\s*\}\}$/, " }}")
}

/**
 * Given a tag wire-string (e.g. `{{ profile.first }}`), pull just
 * the dotted key out (`profile.first`). Used by the TipTap node
 * for its `data-mt` attribute.
 */
export function mergeTagKey(value: string): string {
  const m = MERGE_TAG_RE.exec(value)
  MERGE_TAG_RE.lastIndex = 0
  return m ? m[1] : value
}

/**
 * Context for the active merge-tag registry. Provided by
 * `<EmailBuilder>` so deeply-nested inspector fields can pull
 * the list without prop drilling. `null` (the default) means the
 * host hasn't enabled merge tags — components should hide their
 * pickers in that case.
 */
export const MergeTagsContext = createContext<MergeTagRegistry | null>(null)

export function useMergeTags(): MergeTagRegistry | null {
  return useContext(MergeTagsContext)
}
